import { TIMEZONES } from "@/lib/payPeriod";

export const fmtMoney = (c) => `${c < 0 ? "-" : ""}$${(Math.abs(c || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDuration = (secs) => {
  if (!secs || secs < 0) return "0h 0m";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${m}m`;
};

// Same as fmtDuration but snapped to the nearest 15 minutes (payroll rounding),
// so recorded shift times display as :00 / :15 / :30 / :45.
export const fmtDuration15 = (secs) => {
  const rounded = Math.round(Math.max(0, secs || 0) / 900) * 900;
  return fmtDuration(rounded);
};

// Company time zone (set from the signed-in company); undefined = this device's zone.
let zone;
export const setTimeZone = (tz) => { zone = tz || undefined; };
export const timeZone = () => zone;

const isDay = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    if (isDay(iso)) return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { ...opts, timeZone: "UTC" });
    return new Date(iso).toLocaleDateString(undefined, { ...opts, timeZone: zone });
  } catch { return iso; }
};

export const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: zone });
  } catch { return iso; }
};

// Clock time snapped to the nearest 15 minutes (for the rounded display).
export const fmtTime15 = (iso) => {
  if (!iso) return "—";
  const ms = 15 * 60 * 1000;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  return fmtTime(new Date(Math.round(t / ms) * ms).toISOString());
};

export const fmtDateTime = (iso) => (iso ? `${fmtDate(iso)} · ${fmtTime(iso)}` : "—");

// Wall-clock "YYYY-MM-DDTHH:MM" of a moment in the company time zone.
const wall = (t) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(t)).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// Today's date (YYYY-MM-DD) in the company time zone.
export const todayLocal = () => wall(Date.now()).slice(0, 10);

// Company-zone date (YYYY-MM-DD) of an ISO timestamp.
export const dayOf = (iso) => (iso ? wall(iso).slice(0, 10) : "");

// ISO -> "YYYY-MM-DDTHH:MM" in the company time zone, for datetime-local inputs.
export const toZoneInput = (iso) => (iso ? wall(iso) : "");

// "YYYY-MM-DDTHH:MM" read as company wall-clock time -> Date (Invalid Date if malformed).
export const fromZoneInput = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(s || "");
  if (!m) return new Date(NaN);
  const guess = Date.UTC(+m[1], m[2] - 1, +m[3], +m[4], +m[5]);
  const offset = (t) => Date.parse(`${wall(t)}:00Z`) - Math.floor(t / 60000) * 60000;
  const first = guess - offset(guess);
  return new Date(guess - offset(first));
};

// Note shown when this device's clock differs from the company time zone, else "".
export const zoneNote = () => {
  if (!zone) return "";
  const now = Date.now();
  const device = new Date(now).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  if (wall(now).slice(11) === device) return "";
  return `Times are shown in company time (${TIMEZONES.find(([z]) => z === zone)?.[1] || zone}).`;
};

const JOB_TYPE_LABELS = { standard: "Standard", delivery: "Delivery", site_clean: "Site Clean" };

// A job's type name (from the company's own list) and the kind that decides its photos and checks.
export const jobTypeLabel = (j) => j?.job_type_label || JOB_TYPE_LABELS[j?.job_type] || j?.job_type || "";
export const jobKind = (j) => j?.job_kind || (["delivery", "site_clean"].includes(j?.job_type) ? j.job_type : "standard");
